import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { fmtNum } from '../lib/util.js';
import WorldMapGame from './WorldMapGame.jsx';

// รางวัลเฉพาะแต่ละเมือง (ตามลำดับหมุด เริ่มกรุงเทพฯ)
const CITY_REWARDS = [
  { food: 'tea',       foodName: 'ชาไทยเย็น',          friend: 'friend2', friendName: 'พี่ปั่น คนขับเรือ' },
  { food: 'dessert',   foodName: 'ขนมชั้นโบราณ',        friend: 'friend1', friendName: 'แม่หญิงมาลี' },
  { food: 'drink',     foodName: 'น้ำใบเตยต้นน้ำ',      friend: 'friend3', friendName: 'คุณนายทองคำ' },
  { food: 'mango',     foodName: 'มะม่วงน้ำดอกไม้',     friend: 'friend4', friendName: 'นายพราน ชายแดน' },
  { food: 'goldsweet', foodName: 'ทองหยอดเมืองเหนือ',   friend: 'friend5', friendName: 'เจ้าหนุมาน' },
  { food: 'dessert',   foodName: 'ขนมหวานล้านนา',       friend: 'friend3', friendName: 'แม่ญิงเจียงใหม่' },
];

/** ก้อน 4 — community km + World Map + branch race */
export default function Team({ auth, char }) {
  const [data, setData] = useState({ status: 'loading' });
  const [arrival, setArrival] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api('getTeam', auth);
        setData({ status: 'ready', res });
        // popup ฉลองเมื่อทีมเพิ่งเดินถึงเมืองใหม่ (server แจ้งใน newlyUnlocked)
        const justName = res.newlyUnlocked && res.newlyUnlocked[0];
        if (justName) {
          const idx = res.pins.findIndex((p) => p.name === justName);
          if (idx >= 0) setArrival({ pin: res.pins[idx], i: idx });
        }
      } catch (e) {
        setData({ status: 'error', error: String(e.message || e) });
      }
    })();
  }, [auth]);

  if (data.status === 'loading') return <main className="app center"><p className="hint">กำลังโหลด…</p></main>;
  if (data.status === 'error') return <main className="app center"><p className="err">{data.error}</p></main>;

  const { season, totalKm, branches, pins, nextPin, myBranch, myChar } = data.res;

  if (!season) {
    return <main className="app center"><div className="logo big">🗺️</div><p className="hint">ยังไม่มีซีซั่นที่เปิดอยู่</p></main>;
  }

  const maxBranch = Math.max(1, ...branches.map((b) => b.steps));

  return (
    <main className="app">
      <div className="season-banner">
        <p className="sb-name">{season.name}</p>
        <p className="sb-km">พวกเราเดินไปแล้ว {totalKm} กม.</p>
      </div>

      {/* World Map — Phaser canvas island (เลื่อนแนวตั้ง, อาคารไทยเป็นหมุด) */}
      <section className="card">
        <h3 className="card-title">🗺️ เส้นทาง — ออกเดินจากกรุงเทพฯ มุ่งสู่ล้านนา</h3>
        <WorldMapGame pins={pins} totalKm={totalKm} chosenChar={char || myChar} />
        <p className="map-hint">ลากขึ้น–ลง เพื่อดูทั้งเส้นทางถึงเชียงใหม่ ⬆️</p>
        {nextPin && (
          <p className="next-pin">
            หมุดถัดไป: <b>{nextPin.name}</b> · อีก {Math.max(0, (nextPin.target - totalKm)).toFixed(1)} กม.
          </p>
        )}
        <ul className="route">
          {pins.map((p, i) => (
            <li key={i} className={p.unlocked ? 'unlocked click' : 'locked'}
              onClick={() => p.unlocked && setArrival({ pin: p, i })}>
              <span className="pin-dot">{p.unlocked ? '📍' : '⚪'}</span>
              <div className="pin-body">
                <div className="pin-top">
                  <b>{p.name}</b>
                  <span className="pin-km">{p.target} กม.</span>
                </div>
                {p.unlocked && p.trivia && <p className="pin-trivia">{p.trivia}</p>}
                {p.unlocked && p.cause && <p className="pin-cause">💚 บริจาค: {p.cause}</p>}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Branch race */}
      <section className="card">
        <h3 className="card-title">🏆 สาขาแข่งกัน</h3>
        {branches.length === 0 && <p className="hint">ยังไม่มีข้อมูลก้าวในซีซั่นนี้</p>}
        <ul className="race">
          {branches.map((b, i) => (
            <li key={i} className={b.branch === myBranch ? 'mine' : ''}>
              <span className="rank">{i + 1}</span>
              <div className="race-body">
                <div className="race-top">
                  <b>{b.branch}{b.branch === myBranch ? ' (ของฉัน)' : ''}</b>
                  <span>{b.km} กม.</span>
                </div>
                <div className="bar sm"><div className="bar-fill" style={{ width: `${(b.steps / maxBranch) * 100}%` }} /></div>
              </div>
            </li>
          ))}
        </ul>
        <p className="race-foot">รวม {fmtNum(branches.reduce((s, b) => s + b.steps, 0))} ก้าว</p>
      </section>

      {arrival && <ArrivalPopup arrival={arrival} onClose={() => setArrival(null)} />}
    </main>
  );
}

function ArrivalPopup({ arrival, onClose }) {
  const { pin, i } = arrival;
  const rw = CITY_REWARDS[i % CITY_REWARDS.length];
  return (
    <div className="arrival" onClick={onClose}>
      <div className="arrival-card" onClick={(e) => e.stopPropagation()}>
        <div className="sparkles"><i /><i /><i /><i /><i /><i /></div>
        <p className="ar-badge">🎉 ทีมเดินทางถึง</p>
        <h2 className="ar-name">{pin.name}</h2>
        {pin.trivia && <p className="ar-trivia">{pin.trivia}</p>}
        <div className="ar-rewards">
          <div className="ar-item">
            <img src={`/assets/items/${rw.food}.png`} alt={rw.foodName} />
            <span>ของฝาก</span><b>{rw.foodName}</b>
          </div>
          <div className="ar-item">
            <img src={`/assets/characters/${rw.friend}.png`} alt={rw.friendName} />
            <span>เพื่อนใหม่</span><b>{rw.friendName}</b>
          </div>
        </div>
        {pin.cause && <p className="ar-cause">💚 บริษัทบริจาค: {pin.cause}</p>}
        <button className="btn" onClick={onClose}>เยี่ยมมาก! 🚶</button>
      </div>
    </div>
  );
}
